import GiftOrderApi from '@apis/gift/GiftOrderApi';
import giftBoxQueries from '@apis/queries/giftBoxQueries';
import giftOrderQueries from '@apis/queries/giftOrderQueries';
import AppModal from '@components/AppModal';
import Text from '@components/Text';
import colors from '@constants/colors';
import useCurrency from '@hooks/item/useCurrency';
import { ErrorResponse } from '@models/error';
import { ActivityComponentType, useActivity, useFlow } from '@stackflow/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { giftRefundToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { AxiosError } from 'axios';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Params {
    giftOrderId: string;
    giftOrderNo: string;
}

declare module '@stackflow/config' {
    interface Register {
        CancelPaymentModal: Params;
    }
}

const CancelPaymentModal: ActivityComponentType<'CancelPaymentModal'> = ({ params }) => {
    const { t } = useTranslation('gift');
    const { pop } = useFlow();
    const { isActive } = useActivity();
    const canNavigate = useRef(isActive);

    useLayoutEffect(() => {
        canNavigate.current = isActive;
        return () => {
            canNavigate.current = false;
        };
    }, [isActive]);
    const queryClient = useQueryClient();
    const currency = useCurrency();
    const { data: order } = useQuery({
        ...giftOrderQueries.result(params.giftOrderNo),
        enabled: !!params.giftOrderNo,
    });
    const { mutate, isPending } = useMutation({
        mutationFn: () => GiftOrderApi.cancelGiftOrder(params.giftOrderId),
        onSuccess: async () => {
            pushEcommerceEvent('refund', giftRefundToGtmFormat({
                giftOrderNo: params.giftOrderNo, order, currency,
            }));
            if (canNavigate.current) pop();
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: giftOrderQueries._def }),
                queryClient.invalidateQueries({ queryKey: giftBoxQueries._def }),
            ]);
            toast.success(t('giftSent.cancel.success'));
        },
        onError: (e: AxiosError<ErrorResponse>) => {
            toast.error(e.response?.data?.message || t('giftSent.cancel.failed'));
        },
    });

    return (
        <AppModal title={t('giftCancelModal.title')}
            deny={t('giftCancelModal.cancel')}
            confirm={t('giftCancelModal.confirm')}
            onConfirm={() => mutate()}
            confirmDisabled={isPending}
            denyDisabled={isPending}
            disableOutside={isPending} >
            <Text size='body-short' color={colors.textSecondary} align='center'>
                {t('giftCancelModal.description')}
            </Text>
        </AppModal>
    );
};

export default CancelPaymentModal;
